/**
 * React Query hooks for the Cockpit Netmon (Network Monitor) tool.
 *
 *   useNetmonSites()                       GET /api/v1/netmon/sites
 *   useNetmonSamples(siteId, from, to, t)  GET /api/v1/netmon/sites/{id}/samples
 *   useNetmonEvents(siteId, from, to)      GET /api/v1/netmon/sites/{id}/events
 *   useAckNetmonEvent()                    PATCH /api/v1/netmon/events/{id}/ack
 *   useNetmonDevices(siteId, since)        GET /api/v1/netmon/sites/{id}/devices
 *   useUpdateNetmonSite(siteId)            PATCH /api/v1/netmon/sites/{id}
 *
 *   useSnipeItUnmatched(filters)           GET /api/v1/netmon/snipeit/unmatched
 *   useSnipeItConflicts(siteId)            GET /api/v1/netmon/snipeit/conflicts
 *   useSnipeItMatchCandidates(mac)         GET /api/v1/netmon/snipeit/match-candidates
 *   useSnipeItCreate()                     POST /api/v1/netmon/snipeit/create
 *   useSnipeItResolveConflict()            POST /api/v1/netmon/snipeit/resolve-conflict
 *
 * RBAC (binary access-grant model, spec 00_access-binary-grants.md): any
 * signed-in employee can read. Admin actions (site PATCH, event ack, Snipe-IT
 * create / bulk-by-oui / resolve-conflict) require the it.netmon access slug,
 * the single slug every netmon api route gates on. `hasNetmonRead` always
 * passes; `hasNetmonAdmin` checks it.netmon and bypasses for super-admins.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { timeAgo } from '@ampersandbrands/ui';
import { apiGet, apiPatch, apiPost } from './api';
import { API_BASE } from './apiBase';
import { formatDateTime as _formatDateTime } from './dateUtils';

export const NETMON_API_BASE = `${API_BASE}/v1/netmon`;

// ----- Shared types --------------------------------------------------------

// Four tiers. 'red' is "Down": the store's internet is down, per the
// provider-side check (UniFi cloud, Cato, or a ping from the Otto PC). 'orange' is
// "Critical": heavy loss seen by the store PC in the last 15 minutes.
// 'yellow' is "Degraded": light loss, high latency, DNS/TCP failures, or a
// gateway flap. A store PC that stops reporting is never Down. The
// authoritative logic is the api repo's tools/netmon/src/health.ts.
export type Health = 'green' | 'yellow' | 'orange' | 'red';

export type Transport = 'cellular' | 'wired' | 'unknown';

// Why a store that is listed is not actively probed. Null when monitored.
// See tools/netmon/lambdas/api-sites/index.ts for how these are assigned.
export type ReasonNotMonitored =
  | 'not_enrolled'
  | 'boh_offline'
  | 'no_rmm_agent'
  | 'off_cato_fabric'
  | 'closed';

export type IspProvider = 'uniti' | 'granite' | 'other';

/** Display label for a site's internet provider; a dash when unknown. */
export function ispProviderLabel(p: IspProvider | null | undefined): string {
  if (p === 'uniti') return 'Uniti';
  if (p === 'granite') return 'Granite';
  if (p === 'other') return 'Other';
  return '-';
}

export type NetmonSite = {
  site_id: string;
  site_name: string;
  wan_type: string;
  transport: Transport;
  cellular_primary: number;
  active: number;
  /**
   * true for a normally-monitored store, false for a store that is listed but
   * not actively probed. When false, `health` is meaningless (it will look
   * stale/red) and the UI shows `reason_not_monitored` instead of a tier badge.
   */
  monitored: boolean;
  /**
   * When `monitored` is false, why the store is not probed. Null when monitored.
   */
  reason_not_monitored: ReasonNotMonitored | null;
  /**
   * 1 when the site is one of the 16 Lolli & Pops stores in Project Possible
   * (Steven's strategic focus list). Seeded by netmon migration 0003. The
   * /it/netmon page exposes a filter pill and a per-row badge keyed off this.
   */
  project_possible: number;
  /**
   * Internet provider for the store. Optional so the UI works before the api
   * returns it; null when not yet classified.
   */
  isp_provider?: IspProvider | null;
  created_at: string;
  updated_at: string;
  last_sample_at: string | null;
  worst_loss_pct: number | null;
  worst_latency_ms: number | null;
  any_dns_failure: number;
  any_tcp_unreached: number;
  recent_flap_down: number;
  health: Health;
  /**
   * Internet status from the provider-side check. 'down' is what makes a store
   * red; 'unknown' (no check in 15 minutes, or the check could not tell) shows
   * a grey note and is never red. Optional for older api responses.
   */
  internet_status?: InternetStatus;
  /** Which check decided internet_status: 'unifi', 'cato', 'ping', or null. */
  internet_source?: 'unifi' | 'cato' | 'ping' | null;
  /** When down: time of the first failed check in the current outage. */
  internet_since?: string | null;
  /** When unknown: plain-English reason, e.g. "Router blocks ping". */
  internet_reason?: string | null;
  /** false when the store PC has sent nothing for 20+ minutes. */
  pc_reporting?: boolean;
  /** Newest store PC sample (same value as last_sample_at). */
  pc_last_sample_at?: string | null;
};

export type InternetStatus = 'up' | 'down' | 'unknown';

export type NetmonSample = {
  id: number;
  site_id: string;
  collected_at: string;
  target: string;
  test_type: 'icmp' | 'dns' | 'tcp' | 'http' | 'bandwidth' | 'lan_count';
  avg_latency_ms: number | null;
  max_latency_ms: number | null;
  jitter_ms: number | null;
  loss_pct: number | null;
  dns_resolve_ms: number | null;
  dns_success: number | null;
  tcp_connected: number | null;
  tcp_latency_ms: number | null;
  http_status: number | null;
  http_latency_ms: number | null;
  bytes_sent_per_sec: number | null;
  bytes_recv_per_sec: number | null;
  throughput_mbps: number | null;
  lan_device_count: number | null;
};

export type NetmonEvent = {
  id: number;
  site_id: string;
  detected_at: string;
  event_type: string;
  direction: string | null;
  detail: string | null;
  acknowledged: number;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
};

export type NetmonDevice = {
  mac: string;
  ip: string | null;
  hostname: string | null;
  oui_vendor: string | null;
  interface_alias: string | null;
  first_seen: string | null;
  last_seen: string | null;
  snipeit_asset_id: number | null;
  reconcile_status: string | null;
};

type Envelope<T> = { data: T };

// ----- Sites --------------------------------------------------------------

export function useNetmonSites(): {
  sites: NetmonSite[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<Envelope<NetmonSite[]>, Error>;
} {
  const query = useQuery<Envelope<NetmonSite[]>, Error>({
    queryKey: ['netmon', 'sites'],
    queryFn: () => apiGet<Envelope<NetmonSite[]>>(`${NETMON_API_BASE}/sites`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  return {
    sites: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

// ----- Samples ------------------------------------------------------------

export type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

export function timeRangeToBounds(range: TimeRange): { from: string; to: string } {
  const to = new Date();
  const ms: Record<TimeRange, number> = {
    '1h': 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };
  const from = new Date(to.getTime() - ms[range]);
  return { from: from.toISOString(), to: to.toISOString() };
}

export type NetmonSamplesResponse = {
  site_id: string;
  from: string;
  to: string;
  test_type: string;
  samples: NetmonSample[];
  row_count: number;
  truncated: boolean;
};

export function useNetmonSamples(
  siteId: string | undefined,
  range: TimeRange,
  testType?: string,
): UseQueryResult<Envelope<NetmonSamplesResponse>, Error> {
  return useQuery<Envelope<NetmonSamplesResponse>, Error>({
    queryKey: ['netmon', 'samples', siteId, range, testType ?? 'all'],
    queryFn: () => {
      const { from, to } = timeRangeToBounds(range);
      const qs = new URLSearchParams({ from, to });
      if (testType && testType !== 'all') qs.set('test_type', testType);
      return apiGet<Envelope<NetmonSamplesResponse>>(
        `${NETMON_API_BASE}/sites/${encodeURIComponent(siteId ?? '')}/samples?${qs.toString()}`,
      );
    },
    enabled: !!siteId,
    staleTime: 10_000,
    refetchInterval: 60_000,
  });
}

// ----- Events -------------------------------------------------------------

export type NetmonEventsResponse = {
  site_id: string;
  from: string;
  to: string;
  events: NetmonEvent[];
  row_count: number;
  truncated: boolean;
};

export function useNetmonEvents(
  siteId: string | undefined,
  range: TimeRange,
  filter?: { type?: string; ack?: 0 | 1 },
): UseQueryResult<Envelope<NetmonEventsResponse>, Error> {
  return useQuery<Envelope<NetmonEventsResponse>, Error>({
    queryKey: ['netmon', 'events', siteId, range, filter],
    queryFn: () => {
      const { from, to } = timeRangeToBounds(range);
      const qs = new URLSearchParams({ from, to });
      if (filter?.type) qs.set('type', filter.type);
      if (filter?.ack === 0 || filter?.ack === 1) qs.set('ack', String(filter.ack));
      return apiGet<Envelope<NetmonEventsResponse>>(
        `${NETMON_API_BASE}/sites/${encodeURIComponent(siteId ?? '')}/events?${qs.toString()}`,
      );
    },
    enabled: !!siteId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useAckNetmonEvent() {
  const qc = useQueryClient();
  return useMutation<Envelope<{ event_id: number }>, Error, number>({
    mutationFn: (eventId) =>
      apiPatch<Envelope<{ event_id: number }>>(
        `${NETMON_API_BASE}/events/${eventId}/ack`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['netmon', 'events'] });
    },
  });
}

// ----- Devices ------------------------------------------------------------

export type NetmonDevicesResponse = {
  site_id: string;
  since: string;
  devices: NetmonDevice[];
  row_count: number;
  truncated: boolean;
};

export function useNetmonDevices(
  siteId: string | undefined,
  sinceIso?: string,
): UseQueryResult<Envelope<NetmonDevicesResponse>, Error> {
  return useQuery<Envelope<NetmonDevicesResponse>, Error>({
    queryKey: ['netmon', 'devices', siteId, sinceIso ?? 'default'],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (sinceIso) qs.set('since', sinceIso);
      return apiGet<Envelope<NetmonDevicesResponse>>(
        `${NETMON_API_BASE}/sites/${encodeURIComponent(siteId ?? '')}/devices?${qs.toString()}`,
      );
    },
    enabled: !!siteId,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

// ----- Site update --------------------------------------------------------

export type SiteUpdateInput = Partial<{
  site_name: string;
  wan_type: 'fiber' | 'cable' | 'cellular' | 'sase';
  transport: Transport;
  cellular_primary: boolean;
  active: boolean;
}>;

export function useUpdateNetmonSite(siteId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<Envelope<NetmonSite>, Error, SiteUpdateInput>({
    mutationFn: (input) =>
      apiPatch<Envelope<NetmonSite>>(
        `${NETMON_API_BASE}/sites/${encodeURIComponent(siteId ?? '')}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['netmon', 'sites'] });
    },
  });
}

// ----- Snipe-IT ----------------------------------------------------------

export type OuiSiblingSummary = {
  count: number;
  dominant_label: string;
  dominant_count: number;
};

export type SnipeItUnmatchedRow = {
  site_id: string;
  mac: string;
  ip: string | null;
  hostname: string | null;
  oui_vendor: string | null;
  first_seen: string | null;
  last_seen: string | null;
  oui_siblings: OuiSiblingSummary | null;
};

export type SnipeItUnmatchedResponse = {
  devices: SnipeItUnmatchedRow[];
  total_count: number;
  limit: number;
  offset: number;
};

export function useSnipeItUnmatched(filters: {
  siteId?: string;
  limit?: number;
  offset?: number;
}): UseQueryResult<Envelope<SnipeItUnmatchedResponse>, Error> {
  return useQuery<Envelope<SnipeItUnmatchedResponse>, Error>({
    queryKey: ['netmon', 'snipeit', 'unmatched', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.siteId) qs.set('site_id', filters.siteId);
      qs.set('limit', String(filters.limit ?? 200));
      qs.set('offset', String(filters.offset ?? 0));
      return apiGet<Envelope<SnipeItUnmatchedResponse>>(
        `${NETMON_API_BASE}/snipeit/unmatched?${qs.toString()}`,
      );
    },
    staleTime: 30_000,
  });
}

export type SnipeItConflictRow = {
  mac: string;
  site_id?: string | null;
  site_ids?: string[];
  site_count?: number;
  ip?: string | null;
  hostname?: string | null;
  oui_vendor?: string | null;
  last_seen: string | null;
  reason: 'snipeit_ambiguous' | 'cross_site_collision';
};

export type SnipeItConflictsResponse = { conflicts: SnipeItConflictRow[] };

export function useSnipeItConflicts(
  siteId?: string,
): UseQueryResult<Envelope<SnipeItConflictsResponse>, Error> {
  return useQuery<Envelope<SnipeItConflictsResponse>, Error>({
    queryKey: ['netmon', 'snipeit', 'conflicts', siteId ?? 'all'],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (siteId) qs.set('site_id', siteId);
      return apiGet<Envelope<SnipeItConflictsResponse>>(
        `${NETMON_API_BASE}/snipeit/conflicts?${qs.toString()}`,
      );
    },
    staleTime: 30_000,
  });
}

export type SnipeItCandidate = {
  asset_id: number;
  asset_tag?: string;
  name: string | null;
  serial: string | null;
  model_name: string | null;
  status: string | null;
  assigned_to: string | null;
  last_checkout: string | null;
  rank: 'mac_exact' | 'hostname' | 'ip';
};

export type SnipeItMatchCandidatesResponse = {
  mac: string;
  discovery_hint: { ip: string | null; hostname: string | null };
  mac_field_used: string | null;
  candidates: SnipeItCandidate[];
  snipeit_error: string | null;
};

export function useSnipeItMatchCandidates(
  mac: string | undefined,
): UseQueryResult<Envelope<SnipeItMatchCandidatesResponse>, Error> {
  return useQuery<Envelope<SnipeItMatchCandidatesResponse>, Error>({
    queryKey: ['netmon', 'snipeit', 'match-candidates', mac],
    queryFn: () =>
      apiGet<Envelope<SnipeItMatchCandidatesResponse>>(
        `${NETMON_API_BASE}/snipeit/match-candidates?mac=${encodeURIComponent(mac ?? '')}`,
      ),
    enabled: !!mac,
    staleTime: 30_000,
  });
}

export type SnipeItCreateInput = {
  mac: string;
  site_id: string;
  ip?: string;
  hostname?: string;
  oui_vendor?: string;
  name?: string;
  model_id: number;
  status_id: number;
  location_id?: number;
};

export function useSnipeItCreate() {
  const qc = useQueryClient();
  return useMutation<
    Envelope<{ snipeit_asset_id: number; mac: string }>,
    Error,
    SnipeItCreateInput
  >({
    mutationFn: (input) =>
      apiPost<Envelope<{ snipeit_asset_id: number; mac: string }>>(
        `${NETMON_API_BASE}/snipeit/create`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['netmon', 'snipeit', 'unmatched'] });
      void qc.invalidateQueries({ queryKey: ['netmon', 'devices'] });
    },
  });
}

export type SnipeItResolveInput = {
  mac: string;
  site_id: string;
  chosen_snipeit_asset_id: number;
  other_asset_ids?: number[];
};

export function useSnipeItResolveConflict() {
  const qc = useQueryClient();
  return useMutation<Envelope<unknown>, Error, SnipeItResolveInput>({
    mutationFn: (input) =>
      apiPost<Envelope<unknown>>(
        `${NETMON_API_BASE}/snipeit/resolve-conflict`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['netmon', 'snipeit'] });
      void qc.invalidateQueries({ queryKey: ['netmon', 'devices'] });
    },
  });
}

// ----- Bulk-by-OUI: "label one device, apply to all sibling MACs" --------

export type BulkByOuiInput = {
  oui_prefix: string;        // 6 hex chars, separators OK
  name_template: string;     // {site_id} is substituted per row
  model_id: number;
  status_id: number;
  dry_run?: boolean;
};

export type BulkByOuiResponse = {
  oui_prefix: string;
  dry_run?: boolean;
  targets: { mac: string; site_id: string }[];
  preview_names?: { site_id: string; name: string }[];
  created?: { mac: string; site_id: string; snipeit_asset_id: number; name: string }[];
  skipped?: { mac: string; site_id: string; reason: string }[];
  errors?: { mac: string; site_id: string; message: string }[];
};

// ----- Snipe-IT pickers (model / status / location dropdowns) ----------

export type SnipeItPickers = {
  models: { id: number; name: string; category_name: string | null }[];
  statuses: { id: number; name: string; type: string | null }[];
  locations: { id: number; name: string }[];
};

export function useSnipeItPickers(): UseQueryResult<Envelope<SnipeItPickers>, Error> {
  return useQuery<Envelope<SnipeItPickers>, Error>({
    queryKey: ['netmon', 'snipeit', 'pickers'],
    queryFn: () => apiGet<Envelope<SnipeItPickers>>(`${NETMON_API_BASE}/snipeit/pickers`),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSnipeItBulkByOui() {
  const qc = useQueryClient();
  return useMutation<Envelope<BulkByOuiResponse>, Error, BulkByOuiInput>({
    mutationFn: (input) =>
      apiPost<Envelope<BulkByOuiResponse>>(
        `${NETMON_API_BASE}/snipeit/bulk-by-oui`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['netmon', 'snipeit', 'unmatched'] });
      void qc.invalidateQueries({ queryKey: ['netmon', 'devices'] });
    },
  });
}

// ----- RBAC --------------------------------------------------------------

// Placeholder for viewer (read is currently open to any signed-in employee).
export const NETMON_VIEWER_GROUP_OID = 'PLACEHOLDER_NETMON_VIEWER';
// Real Entra security group `cockpit-netmon-admin` (created 2026-05-24).
// Members can use the Snipe-IT reconcile add/resolve actions.
export const NETMON_ADMIN_GROUP_OID = 'c8be4d45-59da-49b5-b3b1-0ee3b5660c15';

export function hasNetmonRead(_groups: string[] | undefined): boolean {
  // Spec: any authenticated Cockpit user can read.
  return true;
}

// Binary access-grant model (spec 00_access-binary-grants.md): the Network
// monitor tool is a single grantable slug, 'it.netmon', which every netmon api
// route (reads and writes) gates on. Holding it confers full access, so admin
// actions check that one slug. `perms` is the principal's capability strings
// (aliased through user.groups in useSession), not Entra group OIDs. Super-
// admins bypass every capability check, so this takes an explicit superAdmin
// flag (from user.superAdmin) like the other gating helpers.
// NETMON_ADMIN_GROUP_OID is retained for reference but no longer used for gating.
export function hasNetmonAdmin(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('it.netmon');
}

// ----- Helpers -----------------------------------------------------------

export function healthBadgeClass(h: Health): string {
  if (h === 'green') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  }
  if (h === 'yellow') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  }
  if (h === 'orange') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
  }
  return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
}

export function healthDotClass(h: Health): string {
  if (h === 'green') return 'bg-green-500';
  if (h === 'yellow') return 'bg-amber-500';
  if (h === 'orange') return 'bg-orange-500';
  return 'bg-red-500';
}

// Human-readable label for an unmonitored store's reason. Falls back to a
// generic "Not monitored" for null or any value the UI does not recognize.
export function reasonNotMonitoredLabel(reason: string | null): string {
  switch (reason) {
    case 'not_enrolled':
      return 'Not enrolled';
    case 'boh_offline':
      return 'Back-office PC offline';
    case 'no_rmm_agent':
      return 'No management agent';
    case 'off_cato_fabric':
      return 'Off the Cato network';
    case 'closed':
      return 'Closed';
    default:
      return 'Not monitored';
  }
}

/**
 * "last sample 5 minutes ago". Delegates to the shared `timeAgo`; kept as an
 * export because the netmon pages import it from here.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  return timeAgo(iso) || iso;
}

// Render an ISO timestamp in the user's browser timezone. All backend
// timestamps are UTC; display in local TZ everywhere user-facing.
export function formatLocalTime(iso: string | null): string {
  if (!iso) return '--';
  return _formatDateTime(iso);
}

// Health-sort weight. Higher value sorts first.
// Down > Critical > Degraded > Healthy.
export function healthSortWeight(h: Health): number {
  if (h === 'red') return 4;
  if (h === 'orange') return 3;
  if (h === 'yellow') return 2;
  return 1;
}

// Tooltip reason and the grey internet / store-PC notes live in netmonStatus.ts
// (pure, unit-tested). Re-exported so the netmon pages keep importing from here.
export {
  siteHealthReason,
  internetDownText,
  internetUnknownText,
  pcNotReportingText,
  internetSourceLabel,
} from './netmonStatus';
